import { CheckIcon, PhotoIcon, PlusIcon } from "@heroicons/react/24/outline";

import { ITillStockSearchResult } from "../../models/till/ITillStockSearch";
import classNames from "../../utils/classNames";
import PriceFormat from "../../utils/priceFormat";

interface TillResultCardProps {
  part: ITillStockSearchResult;
  inBasket: boolean;
  onAdd: (partId: string) => void;
  disabled?: boolean;
}

/** One stock item in the till search results, sized for a finger. */
export default function TillResultCard({ part, inBasket, onAdd, disabled }: TillResultCardProps) {
  const vehicleLine = [part.vrm, part.make, part.model].filter(Boolean).join(" · ");

  return (
    <button
      type="button"
      disabled={disabled || inBasket}
      onClick={() => onAdd(part.id)}
      className={classNames(
        "w-full text-left flex items-stretch gap-3 rounded-lg border bg-white p-3 min-h-[7rem] shadow-sm transition",
        inBasket
          ? "border-emerald-300 bg-emerald-50 cursor-default"
          : "border-gray-200 hover:border-primary-400 hover:shadow active:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
      )}
    >
      <div className="shrink-0 flex items-center">
        {part.thumbnail ? (
          <img src={part.thumbnail} alt="" loading="lazy" className="h-24 w-24 rounded-md object-cover bg-gray-100" />
        ) : (
          <div className="h-24 w-24 rounded-md bg-gray-100 flex items-center justify-center text-gray-300">
            <PhotoIcon className="h-10 w-10" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div>
          <p className="text-xs font-mono text-gray-500 truncate">{part.tagNumber ?? "No tag"}{part.location ? ` · ${part.location}` : ""}</p>
          <p className="text-base font-medium text-gray-900 leading-snug line-clamp-2">{part.title ?? "Untitled part"}</p>
          {vehicleLine ? <p className="text-sm text-gray-500 truncate">{vehicleLine}</p> : null}
        </div>

        <div className="flex items-center justify-between gap-2 pt-1">
          <span className="text-lg font-semibold text-gray-900">{part.price != null ? PriceFormat(part.price) : "No price"}</span>
          <span
            className={classNames(
              "inline-flex h-9 w-9 items-center justify-center rounded-full",
              inBasket ? "bg-emerald-600 text-white" : "bg-primary-600 text-white"
            )}
          >
            {inBasket ? <CheckIcon className="h-5 w-5" /> : <PlusIcon className="h-5 w-5" />}
          </span>
        </div>
      </div>
    </button>
  );
}
